import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import BaseSpinner from 'react-loader-spinner'
import styled from 'styled-components'
import BaseButton from 'components/Button'
import { formatDate } from 'utils/dateUtils'
import BaseCard from 'components/Card'
import DropDownMenu, { MenuButton } from 'components/DropDownMenu'
import useBooleanState from 'hooks/useBooleanState'
import { RFP_STATE } from '../../../constants'

const RemoveButton = styled(MenuButton)`
  color: ${({ theme }) => theme.colors.danger};
`
const Spinner = styled(BaseSpinner)`
  padding: 0 20px;
`
const CardSpinner = styled.div`
  position: absolute;
  background: ${({ theme }) => theme.colors.backgroundLight};
  opacity: 0.6;
  width: 385px;
  margin: -10px;
  height: 147px;

  ${Spinner} {
    margin: 50px 150px;
  }
`
const InfoBox = styled.div`
  display: flex;

  div:first-child {
    flex-grow: 2;
  }
`
const Button = styled(BaseButton)`
  width: auto;
  border: none;
  text-align: center;

  svg {
    margin-right: 5px;
  }
`
const Actions = styled.div`
  margin-top: 10px;
  align-self: flex-end;
`
const Card = styled(BaseCard)`
  width: 387px;
`
const Title = styled(Link)`
  display: block;
  font-weight: bold;
  font-size: 18px;
  line-height: 24px;
  color: ${({ theme }) => theme.colors.text};
`
const Line = styled.hr`
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-sizing: border-box;
  box-shadow: ${({ theme }) => theme.shadows.standard};
`

export default function Item({
  rfp,
  url,
  onClickPublish,
  onClickRemove,
}: {
  rfp: RequestForProposal
  url: string
  onClickPublish: () => void
  onClickRemove: () => void
}) {
  const { state, toggleState, closeState } = useBooleanState()
  const onRemove = useCallback(() => {
    onClickRemove()
    closeState()
  }, [closeState, onClickRemove])

  return (
    <Card>
      {rfp.meta.isUpdating && (
        <CardSpinner>
          <Spinner type="TailSpin" width={50} height={50} />
        </CardSpinner>
      )}
      <InfoBox>
        <div>
          <Title to={`${url}/${rfp.id}`}>{rfp.name}</Title>
          <div>
            Start: {formatDate(rfp.contract_start)} - End:{' '}
            {formatDate(rfp.contract_end)}
          </div>
        </div>
        <DropDownMenu isOpen={state} onToggle={toggleState}>
          {rfp.state !== RFP_STATE.PUBLISHED && (
            <RemoveButton onClick={onRemove} disabled={rfp.meta.isUpdating}>
              Remove
            </RemoveButton>
          )}
        </DropDownMenu>
      </InfoBox>

      <Line />

      <InfoBox>
        <div>
          <div>{rfp.lane_count} lanes</div>
          <div>Deadline: {formatDate(rfp.request_deadline)}</div>
          <div>
            {rfp.submission_count} responses / {rfp.carrier_invite_count}{' '}
            carriers
          </div>
        </div>

        {rfp.state === RFP_STATE.DRAFT && (
          <Actions>
            <Button
              variant="main"
              onClick={onClickPublish}
              disabled={rfp.meta.isUpdating}
              data-testid="btn-publish"
            >
              PUBLISH
            </Button>
          </Actions>
        )}
      </InfoBox>
    </Card>
  )
}
